/**
 * The marking screen — `/marking/:officialId`.
 *
 * Authority: `05-reporting-marking.md` §B3a ("the marking bars are presented on a final
 * reporting screen, reached at tournament end") and §B6 (the report chain's `.apptsum`
 * 5fr / 7fr split, which this screen borrows for marking beside report).
 *
 * The panel and the report are fetched independently: a missing report must not stop the
 * TD marking, and a report the API refuses (there is none for a subject nobody reports on)
 * simply leaves that column out.
 *
 * **The report column is umpires only** in this slice, because that is the one place the
 * `TD - ` / `UM - ` serialisation applies — for every other subject the TD authors the
 * whole text and no prefix convention exists.
 */

import { Link, useParams } from "react-router-dom";

import { fetchMarkingPanel, fetchOfficial, fetchReport } from "../api/client.js";
import type { ReportBody } from "../api/types.js";
import { HowToLink, HowToUse } from "../app/HowToUse.js";
import { FailBanner, Skeleton } from "../app/states.js";
import { useAsync } from "../app/useAsync.js";
import { MarkingPanel } from "../features/marking/MarkingPanel.js";
import { ReportPanel } from "../features/marking/ReportPanel.js";
import { TOURNAMENT_REPORT_URL } from "../app/modules.js";
import { usePageKicker } from "../app/pageKicker.js";

/** §B3a's kicker for the final reporting screen, shared with the composer it opens. */
export const FINAL_MARKING_KICKER = "Final Marking · Whole Tournament";

export function MarkingPage(): React.JSX.Element {
  const { officialId } = useParams<{ officialId: string }>();
  const id = officialId ?? "";

  // §B3a states this screen's kicker; module 06's own entry now carries Part A's (§A).
  usePageKicker(FINAL_MARKING_KICKER);

  const official = useAsync(() => fetchOfficial(id), [id]);
  const panel = useAsync(() => fetchMarkingPanel(id), [id]);

  const isUmpire = official.status === "ready" && official.data.role === "Umpire";
  const report = useAsync<ReportBody | null>(
    () => (isUmpire ? fetchReport(id) : Promise.resolve(null)),
    [id, isUmpire],
  );

  const name =
    official.status === "ready" ? official.data.mediaName : `TMS person ${id}`;

  return (
    <>
      <div className="repband">
        <h2>Reporting Section</h2>
        <HowToUse id="marking" title="How to use the Marking Panel">
          <li>
            Drag each bar in the{" "}
            <HowToLink to="marking-panel">Marking Panel</HowToLink> to the mark you want. Marks
            step in tens from 0 to 100, and each one saves on its own — there is no Save
            button.
          </li>
          <li>
            Mark a criterion <b>N/A</b> where it did not apply. Its weight drops out of the
            total rather than scoring zero.
          </li>
          <li>
            Read the <HowToLink to="totals-row">totals row</HowToLink> at the foot of the
            criteria table. <b>Weight in play</b> is how much of the marking scheme is still
            counting; the figure beside it is the mark itself, out of 100. Where a criterion
            has been left alone, the row says so, so a nearly-finished marking cannot read as a
            finished one.
          </li>
          <li>
            <HowToLink to="push-marks">Push Marks To TMS</HowToLink> prints exactly what would
            be sent, line by line, before anything is sent.
          </li>
          <li>
            <b>Copy For TMS</b> and <b>Download Word</b> give you the same record to paste into
            TMS by hand, so no path here depends on TMS being reachable.
          </li>
          <li>
            When the marks are settled, open <b>Report Studio</b> from the buttons in this
            heading band to write the words that go with them.
          </li>
        </HowToUse>
        <span style={{ flex: 1 }} />
        <Link className="linkbtn-ext" to="/marking">
          All Officials
        </Link>
        <Link className="linkbtn-ext" to={`/marking/${id}/report`}>
          Report Studio
        </Link>
        <a
          className="linkbtn-ext"
          href={TOURNAMENT_REPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Tournament Report <span aria-hidden="true">↗</span>
        </a>
      </div>

      {official.status === "error" ? <FailBanner error={official.error} /> : null}

      <div className={isUmpire ? "apptsum apptsum--marking" : undefined} style={{ marginTop: 22 }}>
        <div className="stackcol">
          {panel.status === "loading" ? (
            <section className="panel panel--d1">
              <div className="panel__head">
                <h3 className="panel__title">Marking Panel</h3>
                <span className="panel__note">Loading</span>
              </div>
              <Skeleton rows={9} />
            </section>
          ) : null}
          {panel.status === "error" ? <FailBanner error={panel.error} /> : null}
          {panel.status === "ready" ? (
            <MarkingPanel key={id} officialName={name} initialPanel={panel.data} />
          ) : null}
        </div>

        {isUmpire ? (
          <div className="stackcol">
            {report.status === "loading" ? <Skeleton rows={5} /> : null}
            {report.status === "error" ? <FailBanner error={report.error} /> : null}
            {report.status === "ready" && report.data !== null ? (
              <ReportPanel report={report.data} />
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );
}
